import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAppDispatch } from '../../../store/hooks';
import { showError } from '../../../store/errorDialog/errorDialogSlice';
import { getApiErrorMessage } from '../../../utils/errorMessageUtils';

const HTTP_CONFLICT = 409;

interface BookingWithId {
  id: string;
}

interface UseBookingListArgs<TRequest> {
  /** i18n key prefix for conflict/create/delete error messages, e.g. `patient-booking`. */
  keyPrefix: string;
  createBooking: (payload: TRequest) => Promise<unknown>;
  updateBooking: (id: string, payload: TRequest) => Promise<unknown>;
  deleteBooking: (id: string) => Promise<unknown>;
  /**
   * When provided and it returns true, a 409 conflict points at the whole recurring series
   * (`${keyPrefix}-conflict-recurring-error`) rather than the single time window. Omit for booking
   * types that never recur (e.g. closed days).
   */
  isRecurringPayload?: (payload: TRequest) => boolean;
  /**
   * Whether a 409 gets a booking-specific conflict message. Set false for types the scheduler
   * places (reserved staff time), where there is no overlap/409 path and a failure is generic.
   */
  hasConflictError?: boolean;
}

export interface BookingListController<TResponse, TRequest> {
  editingBooking: TResponse | null;
  deleteTarget: TResponse | null;
  isFormOpen: boolean;
  openCreate: () => void;
  openEdit: (booking: TResponse) => void;
  closeForm: () => void;
  requestDelete: (booking: TResponse) => void;
  cancelDelete: () => void;
  handleSave: (payload: TRequest) => Promise<void>;
  handleConfirmDelete: () => Promise<void>;
}

// Owns the create/edit/delete flow every booking list page repeats: the modal-open + edit-target +
// delete-target state, and the save (with 409-conflict handling) and delete handlers. Each page
// supplies its CRUD hook and i18n prefix; the entity/column wiring stays on the page.
export const useBookingList = <TResponse extends BookingWithId, TRequest>({
  keyPrefix,
  createBooking,
  updateBooking,
  deleteBooking,
  isRecurringPayload,
  hasConflictError = true
}: UseBookingListArgs<TRequest>): BookingListController<TResponse, TRequest> => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<TResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TResponse | null>(null);

  const openCreate = useCallback(() => {
    setEditingBooking(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((booking: TResponse) => {
    setEditingBooking(booking);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingBooking(null);
  }, []);

  const requestDelete = useCallback((booking: TResponse) => setDeleteTarget(booking), []);
  const cancelDelete = useCallback(() => setDeleteTarget(null), []);

  const handleSave = useCallback(
    async (payload: TRequest): Promise<void> => {
      const target = editingBooking;
      try {
        if (target) {
          await updateBooking(target.id, payload);
        } else {
          await createBooking(payload);
        }
        closeForm();
      } catch (e) {
        if (hasConflictError && axios.isAxiosError(e) && e.response?.status === HTTP_CONFLICT) {
          // For a recurring request the clash can lie anywhere in the series, so the message
          // points at the occurrences rather than "that time window".
          const conflictKey = isRecurringPayload?.(payload)
            ? `${keyPrefix}-conflict-recurring-error`
            : `${keyPrefix}-conflict-error`;
          dispatch(showError({ message: t(conflictKey) }));
          return;
        }
        console.error('Failed to save booking:', e);
        const fallback = target ? t('update-error') : t(`${keyPrefix}-create-error`);
        dispatch(showError({ message: getApiErrorMessage(e, fallback) }));
      }
    },
    [
      editingBooking,
      createBooking,
      updateBooking,
      closeForm,
      isRecurringPayload,
      hasConflictError,
      keyPrefix,
      dispatch,
      t
    ]
  );

  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (!deleteTarget) return;
    const idToDelete = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await deleteBooking(idToDelete);
    } catch (e) {
      console.error('Failed to delete booking:', e);
      dispatch(showError({ message: getApiErrorMessage(e, t(`${keyPrefix}-delete-error`)) }));
    }
  }, [deleteTarget, deleteBooking, keyPrefix, dispatch, t]);

  return {
    editingBooking,
    deleteTarget,
    isFormOpen,
    openCreate,
    openEdit,
    closeForm,
    requestDelete,
    cancelDelete,
    handleSave,
    handleConfirmDelete
  };
};
